import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Section, SectionHeading, SectionSubtitle, AnimatedCard } from "./Common";

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 28px;
`;

const Card = styled(AnimatedCard)`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 24px;
  display: grid;
  gap: 18px;
  box-shadow: 0 16px 30px rgba(14, 22, 36, 0.08);
`;

const CardContent = styled.div`
  display: grid;
  gap: 10px;
`;

const DemoContainer = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => (theme.name === "light" ? "rgba(42, 204, 195, 0.08)" : "rgba(42, 204, 195, 0.12)")};
  padding: 18px;
  display: grid;
  gap: 16px;
  min-height: 140px;
`;

const DemoHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const DemoTitle = styled.span`
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.text};
  opacity: 0.8;
`;

const DemoMeta = styled.span`
  font-size: ${({ theme }) => theme.typography.small};
  color: ${({ theme }) => theme.text};
  opacity: 0.7;
`;

const Title = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.h3};
`;

const Description = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.muted};
  line-height: 1.6;
`;

const ToggleRail = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  background: ${({ theme }) => theme.border};
  border-radius: 999px;
  padding: 4px;
  gap: 4px;
  width: fit-content;
`;

const ToggleOption = styled.button`
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  background: ${({ theme, $active }) => ($active ? theme.accent : "transparent")};
  color: ${({ theme, $active }) => ($active ? theme.accentContrast : theme.text)};
  font-size: ${({ theme }) => theme.typography.small};
  padding: 6px 14px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
`;

const MeterTrack = styled.div`
  width: 100%;
  height: 14px;
  border-radius: 999px;
  background: ${({ theme }) => theme.background};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.border};
`;

const MeterProgress = styled.span`
  display: block;
  height: 100%;
  background: ${({ theme }) => theme.accent};
  border-radius: inherit;
  transition: width 0.5s ease;
`;

const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  background: ${({ theme }) => theme.background};
  border: 1px solid ${({ theme }) => theme.border};
  font-size: ${({ theme }) => theme.typography.small};
`;

const ChipRemove = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.muted};
  cursor: pointer;
  font-size: 14px;
  line-height: 1;

  &:hover {
    color: ${({ theme }) => theme.accent};
  }
`;

const TopicCard = styled.button`
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme, $open }) =>
    $open
      ? theme.name === "light"
        ? "rgba(42, 204, 195, 0.14)"
        : "rgba(42, 204, 195, 0.22)"
      : theme.surface};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 14px;
  text-align: left;
  cursor: pointer;
  display: grid;
  gap: 10px;
  transition: background 0.2s ease, border-color 0.2s ease;
  color: ${({ theme }) => theme.text};

  &:hover,
  &:focus-visible {
    background: ${({ theme }) =>
      theme.name === "light" ? "rgba(42, 204, 195, 0.18)" : "rgba(42, 204, 195, 0.3)"};
    outline: none;
    border-color: ${({ theme }) => theme.accent};
  }
`;

const TopicLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  color: inherit;
`;

const TopicKeywords = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Keyword = styled.span`
  padding: 4px 10px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  background: ${({ theme }) =>
    theme.name === "light" ? "rgba(42, 204, 195, 0.18)" : "rgba(42, 204, 195, 0.28)"};
  font-size: ${({ theme }) => theme.typography.small};
  color: ${({ theme }) => theme.text};
  font-weight: 600;
`;

const TopicToggle = styled.span`
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 600;
  color: ${({ theme }) => theme.accent};
`;

const Slider = styled.input`
  width: 100%;
  appearance: none;
  height: 6px;
  border-radius: 999px;
  background: linear-gradient(
    to right,
    ${({ theme }) => theme.accent} 0%,
    ${({ theme }) => theme.accent} var(--slider-fill, 50%),
    ${({ theme }) => theme.border} var(--slider-fill, 50%),
    ${({ theme }) => theme.border} 100%
  );
  cursor: pointer;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 2px solid ${({ theme }) => theme.surface};
    background: ${({ theme }) => theme.accent};
    box-shadow: 0 0 0 4px rgba(42, 204, 195, 0.25);
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 2px solid ${({ theme }) => theme.surface};
    background: ${({ theme }) => theme.accent};
    box-shadow: 0 0 0 4px rgba(42, 204, 195, 0.25);
  }

  &::-webkit-slider-runnable-track {
    height: 6px;
    background: transparent;
  }

  &::-moz-range-track {
    height: 6px;
    background: transparent;
  }
`;

const BlurPreview = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ theme }) => theme.border};
  padding: 16px;
  background: ${({ theme }) => theme.background};
  display: grid;
  gap: 10px;
`;

const BlurText = styled.p`
  margin: 0;
  line-height: 1.6;
  filter: ${({ $blurPx }) => ($blurPx > 0 ? `blur(${$blurPx}px)` : "none")};
  transition: filter 0.2s ease;
`;

const RevealBlock = styled.button`
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 16px;
  text-align: left;
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.text};
  cursor: pointer;
`;

const RevealText = styled.p`
  margin: 0;
  line-height: 1.6;
  font-size: inherit;
  color: ${({ theme }) => theme.text};
  filter: ${({ $blurPx }) => ($blurPx > 0 ? `blur(${$blurPx}px)` : "none")};
  transition: filter 0.2s ease;
`;

const MAX_BLUR_PX = 12;
const DEFAULT_BLUR_PERCENT = 42;

const percentToPx = (percent) => {
  const value = typeof percent === "number" ? percent : Number(percent) || 0;
  const clamped = Math.min(Math.max(value, 0), 100);
  return Math.round((clamped / 100) * MAX_BLUR_PX);
};

function PrivacyDemo() {
  const [mode, setMode] = useState("local");

  return (
    <DemoContainer>
      <DemoHeader>
        <DemoTitle>Privacy control</DemoTitle>
        <DemoMeta>{mode === "local" ? "Local" : "Cloud"}</DemoMeta>
      </DemoHeader>
      <Description style={{ margin: 0 }}>
        {mode === "local"
          ? "Texmu runs entirely on your device. Nothing is uploaded."
          : "No cloud processing at all. Everything happens locally only."}
      </Description>
      <ToggleRail role="radiogroup" aria-label="Privacy mode selector">
        <ToggleOption
          type="button"
          onClick={() => setMode("local")}
          $active={mode === "local"}
          aria-pressed={mode === "local"}
        >
          Local
        </ToggleOption>
        <ToggleOption
          type="button"
          onClick={() => setMode("cloud")}
          $active={mode === "cloud"}
          aria-pressed={mode === "cloud"}
        >
          Cloud
        </ToggleOption>
      </ToggleRail>
    </DemoContainer>
  );
}

function PerformanceDemo() {
  const [ms, setMs] = useState(6);

  useEffect(() => {
    const id = window.setInterval(() => {
      setMs(4 + Math.random() * 4);
    }, 1600);
    return () => window.clearInterval(id);
  }, []);

  const width = `${Math.min((ms / 10) * 100, 100)}%`;

  return (
    <DemoContainer>
      <DemoHeader>
        <DemoTitle>Performance monitor</DemoTitle>
        <DemoMeta>{ms.toFixed(1)} ms</DemoMeta>
      </DemoHeader>
      <MeterTrack aria-hidden="true">
        <MeterProgress style={{ width }} />
      </MeterTrack>
      <Description style={{ margin: 0 }}>
        Executes in under 10&nbsp;ms during typical browsing. The extension is only 1&nbsp;MB.
      </Description>
    </DemoContainer>
  );
}

function BlacklistDemo() {
  const [chips, setChips] = useState(["spoilers", "hot takes", "giveaway"]);

  const handleRemove = (chip) => {
    setChips((prev) => prev.filter((item) => item !== chip));
  };

  return (
    <DemoContainer>
      <DemoHeader>
        <DemoTitle>Word blacklist</DemoTitle>
        <DemoMeta>{chips.length} active</DemoMeta>
      </DemoHeader>
      <ChipList>
        {chips.map((chip) => (
          <Chip key={chip}>
            <span>{chip}</span>
            <ChipRemove type="button" aria-label={`Remove ${chip}`} onClick={() => handleRemove(chip)}>
              ×
            </ChipRemove>
          </Chip>
        ))}
        {chips.length === 0 && <DemoMeta>No blacklist terms yet.</DemoMeta>}
      </ChipList>
    </DemoContainer>
  );
}

function TopicDemo() {
  const [open, setOpen] = useState(false);

  const keywords = useMemo(
    () => ["memecoin", "free Bitcoin", "moonshot", "trading signals"],
    []
  );

  return (
    <DemoContainer>
      <DemoHeader>
        <DemoTitle>Topic filters</DemoTitle>
        <DemoMeta>{open ? "Expanded" : "Collapsed"}</DemoMeta>
      </DemoHeader>
      <TopicCard type="button" onClick={() => setOpen((prev) => !prev)} $open={open}>
        <TopicLabel>
          <span>Crypto</span>
          <TopicToggle>{open ? "Hide" : "Show"}</TopicToggle>
        </TopicLabel>
        {open && (
          <TopicKeywords>
            {keywords.map((keyword) => (
              <Keyword key={keyword}>{keyword}</Keyword>
            ))}
          </TopicKeywords>
        )}
      </TopicCard>
    </DemoContainer>
  );
}

function BlurDemo() {
  const [percent, setPercent] = useState(DEFAULT_BLUR_PERCENT);
  const blurPx = percentToPx(percent);

  return (
    <DemoContainer>
      <DemoHeader>
        <DemoTitle>Blur preview</DemoTitle>
        <DemoMeta>{percent}%</DemoMeta>
      </DemoHeader>
      <BlurPreview>
        <BlurText $blurPx={blurPx}>
          Congratulations! You’ve won a cash prize in our exclusive giveaway.
        </BlurText>
      </BlurPreview>
      <Slider
        type="range"
        min="0"
        max="100"
        step="1"
        value={percent}
        onChange={(event) => setPercent(Number(event.target.value))}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-label="Adjust blur preview strength"
        style={{ "--slider-fill": `${percent}%` }}
      />
    </DemoContainer>
  );
}

function RevealDemo() {
  const [revealed, setRevealed] = useState(false);
  const blurPx = revealed ? 0 : percentToPx(DEFAULT_BLUR_PERCENT);

  return (
    <DemoContainer>
      <DemoHeader>
        <DemoTitle>One-click reveal</DemoTitle>
        <DemoMeta>{revealed ? "Visible" : "Blurred"}</DemoMeta>
      </DemoHeader>
      <RevealBlock type="button" onClick={() => setRevealed((prev) => !prev)}>
        <RevealText $blurPx={blurPx}>
          Sneak peek: “AI-powered spoilers incoming.” Click to toggle visibility.
        </RevealText>
      </RevealBlock>
    </DemoContainer>
  );
}

const featureList = [
  {
    title: "Privacy first",
    text: "All filtering happens locally in your browser. No data leaves your device, ever.",
    Demo: PrivacyDemo
  },
  {
    title: "Lightweight performance",
    text: "Texmu is optimized for size and speed with minimal CPU impact.",
    Demo: PerformanceDemo
  },
  {
    title: "Word blacklist",
    text: "Add words or phrases that trigger automatic blur no matter where they appear.",
    Demo: BlacklistDemo
  },
  {
    title: "Topic filters",
    text: "Group related keywords by topic to understand why something was hidden at a glance.",
    Demo: TopicDemo
  },
  {
    title: "Blur preview",
    text: "Test how strong the blur looks and adjust the intensity in real time before deploying it.",
    Demo: BlurDemo
  },
  {
    title: "One-click reveal",
    text: "Tap to reveal exactly what you need without unblocking your entire feed.",
    Demo: RevealDemo
  }
];

function Features() {
  return (
    <Section id="features">
      <SectionHeading>Everything you need to tame the feed</SectionHeading>
      <SectionSubtitle>
        Texmu: your shield against the things you never want to see (again and again and again).
      </SectionSubtitle>
      <Grid>
        {featureList.map((item, index) => {
          const { Demo } = item;
          return (
            <Card key={item.title} style={{ animationDelay: `${0.1 + index * 0.08}s` }}>
              <CardContent>
                <Title>{item.title}</Title>
                <Description>{item.text}</Description>
              </CardContent>
              <Demo />
            </Card>
          );
        })}
      </Grid>
    </Section>
  );
}

export default Features;
